import React from "react";
import VideoDemo from "./VideoDemo";
import "@progress/kendo-theme-default/dist/all.css";
import "./App.css";
import "./Grid.css";
import "./Card.css";
import "./Button.css";
import "./Menu.css";
import AIAnalyst from "./AiAnalyst";
import SeverityBadge from "./SeverityBadge";
import RealTimeAlertChart from "./RealTimeAlertChart";
import "./dark-theme.css";
import NeonEagle from "./NeonEagle";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Card } from "@progress/kendo-react-layout";
import { Loader } from "@progress/kendo-react-indicators";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
} from "@progress/kendo-react-charts";
import "hammerjs";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { useEffect } from "react";

const toDate = (s) => new Date(s);
const formatDate = (d) => {
  if (!d) return "";
  let dateObj = d;
  if (typeof d === "string" || typeof d === "number") {
    dateObj = new Date(d);
  }
  if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
    return dateObj.toISOString().slice(0, 10);
  }
  // fallback: return as string if invalid
  return typeof d === "string" ? d : "";
};

const BASE_ALERTS = [
  {
    id: 1,
    title: "Phishing attempt",
    severity: "High",
    date: toDate("2025-09-01"),
  },
  {
    id: 2,
    title: "Update required",
    severity: "Medium",
    date: toDate("2025-09-02"),
  },
  {
    id: 3,
    title: "New login detected",
    severity: "Low",
    date: toDate("2025-09-03"),
  },
  {
    id: 4,
    title: "Port scan detected",
    severity: "Medium",
    date: toDate("2025-09-04"),
  },
  {
    id: 5,
    title: "Suspicious file downloaded",
    severity: "High",
    date: toDate("2025-09-05"),
  },
  {
    id: 6,
    title: "Unusual VPN connection",
    severity: "Low",
    date: toDate("2025-09-06"),
  },
  {
    id: 7,
    title: "Admin access attempt",
    severity: "High",
    date: toDate("2025-09-07"),
  },
  {
    id: 8,
    title: "Outdated software detected",
    severity: "Medium",
    date: toDate("2025-09-08"),
  },
  {
    id: 9,
    title: "Password change",
    severity: "Low",
    date: toDate("2025-09-09"),
  },
  {
    id: 10,
    title: "Malicious IP blocked",
    severity: "High",
    date: toDate("2025-09-10"),
  },
  {
    id: 11,
    title: "Unauthorized SSH connection",
    severity: "Medium",
    date: toDate("2025-09-11"),
  },
  {
    id: 12,
    title: "Malware detected",
    severity: "High",
    date: toDate("2025-09-12"),
  },
  {
    id: 13,
    title: "User disabled",
    severity: "Low",
    date: toDate("2025-09-13"),
  },
  {
    id: 14,
    title: "Brute force attempt",
    severity: "High",
    date: toDate("2025-09-14"),
  },
  {
    id: 15,
    title: "Connection to forbidden site",
    severity: "Medium",
    date: toDate("2025-09-15"),
  },

  ...Array.from({ length: 15 }, (_, i) => {
    const day = 16 + i;
    const realisticTitles = [
      "Phishing attempt detected",
      "SQL Injection vulnerability",
      "Malware found",
      "Suspicious login",
      "Privilege escalation attempt",
      "Cross-Site Scripting (XSS)",
      "Ransomware blocked",
      "Brute force attack",
      "Zero-day exploit detected",
      "Data exfiltration attempt",
      "Unusual outbound traffic",
      "Unauthorized access",
      "DDoS attack mitigated",
      "Suspicious file upload",
      "Credential stuffing detected",
      "Remote code execution",
      "Firewall rule change",
      "Unpatched software",
      "Insider threat alert",
    ];
    return {
      id: 16 + i,
      title: realisticTitles[i % realisticTitles.length],
      severity: ["High", "Medium", "Low"][Math.floor(Math.random() * 3)],
      date: toDate(`2025-09-${day <= 30 ? day : 30}`),
    };
  }),
];

async function fetchAlertsApi() {
  // Deterministic refresh for challenge: always return the same dataset
  await new Promise((r) => setTimeout(r, 400));
  return BASE_ALERTS.map((a) => ({ ...a, date: new Date(a.date) }));
}

// Render Date on a single line to avoid truncation (modern cells API)
const DateCell = (props) => {
  const { dataItem, field, tdProps, style, className, colSpan } = props;
  const value = dataItem?.[field] ?? "";
  const baseTd = tdProps || {};
  const mergedClass = [className || baseTd.className, "has-tooltip"]
    .filter(Boolean)
    .join(" ");
  return (
    <td
      {...baseTd}
      style={{ ...(style || baseTd.style), whiteSpace: "nowrap" }}
      className={mergedClass}
      colSpan={colSpan || baseTd.colSpan}
      title={formatDate(value)}
    >
      {formatDate(value)}
    </td>
  );
};

function LiveMonitoring() {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="sora-quadrant sora-quadrant-4"
      style={{ minHeight: "72px" }}
    >
      <div
        className="section-card"
        style={{
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h4 style={{ margin: "0 0 6px 0" }}>📡 Live Monitoring</h4>
        <p
          style={{
            margin: 0,
            color: "#6b7280",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <Loader type="pulsing" size="small" />
          <span>
            🟢 System Active • Last update: {now.toLocaleTimeString()}
          </span>
        </p>
      </div>
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = React.useState(() => {
    try {
      const saved = localStorage.getItem("soccert.darkMode");
      if (saved !== null) {
        return saved === "true";
      }
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        return true;
      }
    } catch (_) {}
    return false;
  });
  const [search, setSearch] = React.useState("");
  const [severityFilter, setSeverityFilter] = React.useState(null);
  const [alerts, setAlerts] = React.useState(() => {
    try {
      const saved = localStorage.getItem("soccert.alerts");
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr)) {
          return arr.map((a) => ({
            ...a,
            date: new Date(a.date || a.timestamp || Date.now()),
          }));
        }
      }
    } catch (_) {}
    return [];
  });
  const [loadingAlerts, setLoadingAlerts] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState(null);
  const [gridKey, setGridKey] = React.useState(0);
  const [refreshCount, setRefreshCount] = React.useState(0);
  const [settingsTab, setSettingsTab] = React.useState(0);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const settingsAnchorRef = React.useRef(null);
  const [autoRefresh, setAutoRefresh] = React.useState(() => {
    try {
      const v = localStorage.getItem("soccert.autoRefresh");
      return v === null ? true : v === "true";
    } catch (_) {
      return true;
    }
  });
  const [showNotifications, setShowNotifications] = React.useState(() => {
    try {
      const v = localStorage.getItem("soccert.showNotifications");
      return v === null ? true : v === "true";
    } catch (_) {
      return true;
    }
  });

  const gridHostRef = React.useRef(null);
  const alertsSectionRef = React.useRef(null);
  const analyticsSectionRef = React.useRef(null);
  const [activeMenu, setActiveMenu] = React.useState("dashboard");
  const [menuFlash, setMenuFlash] = React.useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const scrollToSection = React.useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const focusAlertsSection = React.useCallback(() => {
    const el = document.getElementById("alerts-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("flash-area");
      window.setTimeout(() => el.classList.remove("flash-area"), 900);
    }
    setActiveMenu("alerts");
    setMenuFlash("alerts");
    window.setTimeout(() => setMenuFlash(""), 500);
  }, []);

  const handleMenuSelect = React.useCallback(
    (e) => {
      const text = e?.item?.text || "";
      let key = "";
      if (text.includes("Dashboard")) key = "dashboard";
      else if (text.includes("Alerts")) key = "alerts";
      else if (text.includes("Analytics")) key = "analytics";
      else if (text.includes("Settings")) key = "settings";

      if (!key) return;
      if (key === "dashboard") {
        setActiveMenu("dashboard");
        setMenuFlash("dashboard");
        window.setTimeout(() => setMenuFlash(""), 500);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (key === "analytics") {
        setActiveMenu("analytics");
        setMenuFlash("analytics");
        window.setTimeout(() => setMenuFlash(""), 500);
        // Analytics should point to AI Analysis + Graphs
        scrollToSection("analytics-ai-section");
        setSettingsOpen(false);
      } else if (key === "settings") {
        // Toggle dropdown popup under Settings
        setSettingsOpen((o) => !o);
        // Trigger a brief flash on Settings like other items
        setMenuFlash("settings");
        window.setTimeout(() => setMenuFlash(""), 800);
      } else {
        setActiveMenu("alerts");
        setMenuFlash("alerts");
        window.setTimeout(() => setMenuFlash(""), 500);
        scrollToSection(`${key}-section`);
        setSettingsOpen(false);
      }
      // Close mobile menu after a selection (mobile only CSS handles visibility)
      setMobileMenuOpen(false);
    },
    [scrollToSection]
  );

  const fetchAlerts = React.useCallback(async (byUser = false) => {
    setLoadingAlerts(true);
    try {
      const [data] = await Promise.all([
        fetchAlertsApi(),
        byUser ? new Promise((r) => setTimeout(r, 1200)) : Promise.resolve(),
      ]);
      setAlerts(data);
      try {
        localStorage.setItem("soccert.alerts", JSON.stringify(data));
      } catch (_) {}
      setLastRefreshed(new Date());
      if (byUser) {
        setRefreshCount((c) => c + 1);
      }
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  // (moved below after refreshFromApi is defined)

  // MAPPING SEVERITY
  const mapSeverity = (severity) => {
    const severityMap = {
      Critical: "High",
      High: "High",
      Medium: "Medium",
      Low: "Low",
      Important: "High",
    };
    return severityMap[severity] || "Medium";
  };

  // API refresh function used by manual refresh and auto-refresh
  const refreshFromApi = React.useCallback(async (byUser = false) => {
    setLoadingAlerts(true);
    try {
      let apiAlerts = [];
      try {
        const response = await fetch("/api/alerts");
        apiAlerts = await response.json();
      } catch (err) {
        // ignore fetch error, fallback below
      }
      if (!Array.isArray(apiAlerts) || apiAlerts.length === 0) {
        // fallback local demo data
        apiAlerts = BASE_ALERTS.map((a) => ({ ...a, date: new Date(a.date) }));
      }
      if (Array.isArray(apiAlerts) && apiAlerts.length > 0) {
        const transformedAlerts = apiAlerts.map((alert) => ({
          id: alert.cve_id || alert.id || Date.now(),
          cve_id: alert.cve_id || null,
          title: alert.title,
          severity: mapSeverity(alert.severity),
          date: new Date(alert.timestamp || alert.date),
          link: alert.link || "No link available",
          source: alert.source || "Unknown",
        }));
        // Local CVE dedupe (keep latest)
        const norm = (v) => (v == null ? "" : String(v)).trim().toUpperCase();
        const isCve = (s) => /^CVE-/i.test(String(s || ""));
        const byKey = new Map();
        for (const a of transformedAlerts) {
          const key = a?.cve_id
            ? norm(a.cve_id)
            : typeof a?.id === "string" && isCve(a.id)
            ? norm(a.id)
            : "";
          if (key) {
            const prev = byKey.get(key);
            if (!prev) byKey.set(key, a);
            else {
              const prevTs =
                prev?.date instanceof Date
                  ? prev.date.getTime()
                  : new Date(prev?.date || 0).getTime();
              const currTs =
                a?.date instanceof Date
                  ? a.date.getTime()
                  : new Date(a?.date || 0).getTime();
              byKey.set(key, currTs >= prevTs ? a : prev);
            }
          } else {
            byKey.set(`NO_CVE:${a?.id}-${Math.random()}`, a);
          }
        }
        const uniqueAlerts = Array.from(byKey.values());
        setAlerts(uniqueAlerts);
        try {
          localStorage.setItem("soccert.alerts", JSON.stringify(uniqueAlerts));
        } catch (_) {}
      }
      setLastRefreshed(new Date());
      if (byUser) setRefreshCount((c) => c + 1);
    } catch (_) {
      // keep current alerts on error
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  // Initial load + 10s polling: use the API-based refresher
  React.useEffect(() => {
    refreshFromApi(false);
    const interval = setInterval(() => refreshFromApi(false), 10000);
    return () => clearInterval(interval);
  }, [refreshFromApi]);

  React.useEffect(() => {
    document.body.classList.toggle("dashboard-dark", darkMode);
    try {
      localStorage.setItem("soccert.darkMode", darkMode ? "true" : "false");
    } catch (_) {}
    return () => document.body.classList.remove("dashboard-dark");
  }, [darkMode]);

  React.useEffect(() => {
    try {
      localStorage.setItem(
        "soccert.autoRefresh",
        autoRefresh ? "true" : "false"
      );
    } catch (_) {}
  }, [autoRefresh]);
  React.useEffect(() => {
    try {
      localStorage.setItem(
        "soccert.showNotifications",
        showNotifications ? "true" : "false"
      );
    } catch (_) {}
  }, [showNotifications]);

  React.useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      refreshFromApi(false);
    }, 300000);
    return () => window.clearInterval(id);
  }, [autoRefresh, refreshFromApi]);

  // Find anchor element for Settings popup after menu renders
  React.useEffect(() => {
    const el = document.querySelector(".soc-cert-menu .settings-menu-item");
    if (el) settingsAnchorRef.current = el;
  });

  // Close popup on outside click
  React.useEffect(() => {
    if (!settingsOpen) return;
    const onDocClick = (ev) => {
      const popup = document.querySelector(".settings-popup");
      const anchor = settingsAnchorRef.current;
      if (!popup || !anchor) return;
      if (popup.contains(ev.target) || anchor.contains(ev.target)) return;
      setSettingsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [settingsOpen]);

  let filteredAlerts = alerts;
  if (severityFilter) {
    filteredAlerts = filteredAlerts.filter(
      (a) => a.severity === severityFilter
    );
  }
  if (search.trim()) {
    filteredAlerts = filteredAlerts.filter((a) =>
      a.title.toLowerCase().includes(search.toLowerCase())
    );
  }

  const handleChartClick = React.useCallback((e) => {
    const cat = e?.category;
    if (!cat) return;
    setSeverityFilter((prev) => (prev === cat ? null : cat));
  }, []);

  // Memoize the Threat Distribution chart; refresh for Alerts or data changes, not Settings toggles
  const isAlertsActive = activeMenu === "alerts";
  const threatChart = React.useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          paddingRight: 8,
        }}
      >
        <Chart
          style={{ width: "260px", height: "260px" }}
          onSeriesClick={handleChartClick}
        >
          <ChartSeries>
            <ChartSeriesItem
              type="pie"
              data={[
                {
                  category: "High",
                  value: alerts.filter((a) => a.severity === "High").length,
                  explode: severityFilter === "High",
                },
                {
                  category: "Medium",
                  value: alerts.filter((a) => a.severity === "Medium").length,
                  explode: severityFilter === "Medium",
                },
                {
                  category: "Low",
                  value: alerts.filter((a) => a.severity === "Low").length,
                  explode: severityFilter === "Low",
                },
              ]}
              field="value"
              categoryField="category"
              explodeField="explode"
            />
          </ChartSeries>
        </Chart>
      </div>
    ),
    [alerts, severityFilter, handleChartClick, isAlertsActive]
  );

  const SeverityCell = (props) => {
    const { dataItem, tdProps, style, className, colSpan } = props;
    const baseTd = tdProps || {};
    const mergedClass = [className || baseTd.className, "has-tooltip"]
      .filter(Boolean)
      .join(" ");
    return (
      <td
        {...baseTd}
        style={{ ...(style || baseTd.style), textAlign: "center" }}
        className={mergedClass}
        colSpan={colSpan || baseTd.colSpan}
        title={`${dataItem?.severity || "Unknown"} severity`}
      >
        <SeverityBadge severity={dataItem?.severity} darkMode={darkMode} />
      </td>
    );
  };

  const IDCell = (props) => {
    const { dataItem, tdProps, style, className, colSpan } = props;
    const baseTd = tdProps || {};
    const val = dataItem?.id;
    const isCve = typeof val === "string" && /^CVE-/i.test(val);
    const isTimestampLike = typeof val === "number" && val > 1e12;
    const display = isCve
      ? String(val).toUpperCase()
      : isTimestampLike
      ? `#${String(val).slice(-6)}`
      : val;
    const title = isCve
      ? `CVE ${display}`
      : isTimestampLike
      ? `Auto ID ${val}`
      : `ID ${display}`;
    const mergedClass = [className || baseTd.className, "has-tooltip"]
      .filter(Boolean)
      .join(" ");
    return (
      <td
        {...baseTd}
        style={{
          ...(style || baseTd.style),
          textAlign: isCve ? "left" : "right",
        }}
        className={mergedClass}
        colSpan={colSpan || baseTd.colSpan}
        title={title}
        aria-label={title}
      >
        {display}
      </td>
    );
  };

  const TitleCell = (props) => {
    const { dataItem, tdProps, style, className, colSpan } = props;
    const baseTd = tdProps || {};
    const mergedClass = [className || baseTd.className, "has-tooltip"]
      .filter(Boolean)
      .join(" ");
    return (
      <td
        {...baseTd}
        style={{
          ...(style || baseTd.style),
          maxWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        className={mergedClass}
        colSpan={colSpan || baseTd.colSpan}
        title={dataItem?.title || ""}
        aria-label={dataItem?.title || ""}
      >
        {dataItem?.title}
      </td>
    );
  };

  // Removed effect that manipulated Kendo Grid DOM for tooltips. All tooltip titles are handled by cell renderers and attributes.

  return (
    <div>
      {/* Header Unifié avec Eagle + Menu + Titre */}
      <header className="soc-cert-unified-header">
        <div className="header-logo-section">
          <div className="header-eagle-container">
            <NeonEagle
              color="#00fff7"
              height={60}
              speed={darkMode ? "7s" : "5.5s"}
              alertCount={alerts.length}
              style={{ filter: "drop-shadow(0 0 8px #00fff7)" }}
            />
          </div>
          <div className="header-title-container">
            <h1 className="header-main-title">SOC-CERT</h1>
            <p className="header-subtitle">Security Operations Center</p>
          </div>
        </div>

        <div className="header-menu-section">
          {/* Mobile toggle button (shown via CSS at <710px) */}
          <button
            type="button"
            className="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((o) => !o)}
            style={{ display: "none" }}
          >
            ☰ Menu
          </button>
          <Menu
            className={`soc-cert-menu active-${activeMenu} ${
              menuFlash ? `flash-${menuFlash}` : ""
            } ${settingsOpen ? "settings-open active-settings" : ""} ${
              mobileMenuOpen ? "mobile-open" : ""
            }`}
            onSelect={handleMenuSelect}
          >
            <MenuItem text="📊 Dashboard" />
            <MenuItem text="🚨 Alerts" />
            <MenuItem text="📈 Analytics" />
            <MenuItem text="⚙️ Settings" className="settings-menu-item" />
          </Menu>
          <Popup
            anchor={settingsAnchorRef.current}
            show={settingsOpen}
            anchorAlign={{ horizontal: "center", vertical: "bottom" }}
            popupAlign={{ horizontal: "center", vertical: "top" }}
            appendTo={document.body}
          >
            <div className="settings-popup" style={{ position: "relative" }}>
              <button
                aria-label="Close settings"
                onClick={() => setSettingsOpen(false)}
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  background: "transparent",
                  border: "none",
                  color: darkMode ? "#e5e7eb" : "#4b5563",
                  fontSize: "16px",
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: "4px",
                }}
                title="Close"
              >
                ✕
              </button>
              <div className="section-card" style={{ padding: "16px" }}>
                <h3
                  style={{
                    marginBottom: "16px",
                    color: darkMode ? "#e5e7eb" : "#495057",
                  }}
                >
                  ⚙️ Dashboard Settings
                </h3>
                <TabStrip
                  selected={settingsTab}
                  onSelect={(e) => setSettingsTab(e.selected)}
                  tabContentStyle={darkMode ? { color: "#e5e7eb" } : undefined}
                  // The header tabs themselves need brighter text in dark mode
                  // Wrap TabStrip in a span to scope a color override safely
                >
                  <TabStripTab title="General">
                    <div
                      className="settings-grid"
                      style={darkMode ? { color: "#000" } : undefined}
                    >
                      <div className="setting-group">
                        <h4>🎨 Theme Preferences</h4>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ fontSize: "14px" }}>
                            Current theme:
                          </span>
                          <span
                            style={{
                              fontWeight: "bold",
                              color: darkMode ? "#00fff7" : "#3b82f6",
                            }}
                          >
                            {darkMode ? "Dark Mode" : "Light Mode"}
                          </span>
                          <Button
                            onClick={() => setDarkMode(!darkMode)}
                            style={{ marginLeft: "10px" }}
                          >
                            {darkMode
                              ? "☀️ Switch to Light"
                              : "🌙 Switch to Dark"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TabStripTab>
                  <TabStripTab title="Alerts">
                    <div className="settings-grid">
                      <div className="setting-group">
                        <h4>🔍 Alert Filters</h4>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            onClick={() => {
                              setSeverityFilter(null);
                              focusAlertsSection();
                              setSettingsOpen(false);
                            }}
                            themeColor={
                              !severityFilter ? "primary" : "secondary"
                            }
                          >
                            👁️ Show All Alerts
                          </Button>
                          <Button
                            onClick={() => {
                              setSeverityFilter("High");
                              focusAlertsSection();
                              setSettingsOpen(false);
                            }}
                            themeColor={
                              severityFilter === "High"
                                ? "primary"
                                : "secondary"
                            }
                          >
                            🔴 High Severity Only
                          </Button>
                          <Button
                            onClick={() => {
                              setSeverityFilter("Medium");
                              focusAlertsSection();
                              setSettingsOpen(false);
                            }}
                            themeColor={
                              severityFilter === "Medium"
                                ? "primary"
                                : "secondary"
                            }
                          >
                            🟡 Medium Severity
                          </Button>
                          <Button
                            onClick={() => {
                              setSeverityFilter("Low");
                              focusAlertsSection();
                              setSettingsOpen(false);
                            }}
                            themeColor={
                              severityFilter === "Low" ? "primary" : "secondary"
                            }
                          >
                            🟢 Low Severity Only
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TabStripTab>
                  <TabStripTab title="Data">
                    <div className="settings-grid">
                      <div className="setting-group">
                        <h4>💾 Data Management</h4>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            onClick={() => window.location.reload()}
                            themeColor="warning"
                          >
                            🔄 Reset Dashboard
                          </Button>
                          <Button
                            onClick={() => alert("Export feature coming soon!")}
                            themeColor="info"
                          >
                            📤 Export Data
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TabStripTab>
                  <TabStripTab title="Preferences">
                    <div
                      className="settings-grid"
                      style={darkMode ? { color: "#000" } : undefined}
                    >
                      <div className="setting-group">
                        <h4>👀 Display Preferences</h4>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="checkbox"
                              id="auto-refresh"
                              checked={autoRefresh}
                              onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            <label
                              htmlFor="auto-refresh"
                              style={{ fontSize: "14px" }}
                            >
                              Auto-refresh every 5 minutes
                            </label>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="checkbox"
                              id="show-notifications"
                              checked={showNotifications}
                              onChange={(e) =>
                                setShowNotifications(e.target.checked)
                              }
                            />
                            <label
                              htmlFor="show-notifications"
                              style={{ fontSize: "14px" }}
                            >
                              Show desktop notifications
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabStripTab>
                </TabStrip>
              </div>
            </div>
          </Popup>
        </div>

        <div className="header-actions-section">
          <Button
            className="theme-toggle-btn"
            onClick={() => setDarkMode(!darkMode)}
            style={{
              padding: "8px 16px",
              background: darkMode
                ? "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
                : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "6px",
              border: "none",
            }}
          >
            {darkMode ? "☀️ Light" : "🌙 Dark"}
          </Button>
        </div>
      </header>

      {/* 🎯 STRUCTURE SORA: 50/50 */}
      <div id="dashboard-section" className="sora-dashboard-layout">
        {/* LEFT PANEL: Alert Grid */}
        <div className="sora-left-panel">
          <div
            id="alerts-section"
            className="section-card"
            ref={alertsSectionRef}
          >
            <h2 style={{ margin: 0 }}>🚨 Security Alerts</h2>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <div
                style={{ display: "flex", gap: "20px", alignItems: "center" }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  {filteredAlerts.length} of {alerts.length} alerts
                </span>
                {severityFilter && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "2px 8px",
                      borderRadius: "9999px",
                      border: darkMode
                        ? "1px solid #94a3b8"
                        : "1px solid #cbd5e1",
                      color: darkMode ? "#e5e7eb" : "#374151",
                      background: "transparent",
                      fontSize: "12px",
                    }}
                    aria-label={`Active filter: ${severityFilter}`}
                  >
                    Filter: {severityFilter}
                    <Button
                      look="flat"
                      size="small"
                      aria-label="Clear severity filter"
                      onClick={() => setSeverityFilter(null)}
                    >
                      ✕
                    </Button>
                  </span>
                )}
                {lastRefreshed && (
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                    Last refresh: {lastRefreshed.toLocaleTimeString()}
                  </span>
                )}
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Refreshes: {refreshCount}
                </span>
                <Button
                  size="small"
                  look="outline"
                  aria-label="Clear all filters"
                  onClick={() => {
                    setSearch("");
                    setSeverityFilter(null);
                    setGridKey((k) => k + 1);
                  }}
                >
                  Clear Filters
                </Button>
                <Button
                  size="small"
                  look="flat"
                  aria-label="Reset refresh counter"
                  onClick={() => setRefreshCount(0)}
                >
                  Reset Counter
                </Button>
                <Button
                  size="small"
                  aria-label="Refresh alerts"
                  disabled={loadingAlerts}
                  onClick={() => refreshFromApi(true)}
                >
                  {loadingAlerts ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Loader size="small" type="pulsing" />
                      Refreshing…
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span role="img" aria-hidden="true">
                        🔄
                      </span>
                      Refresh
                    </span>
                  )}
                </Button>
              </div>
            </div>

            <div className="grid-responsive-container" ref={gridHostRef}>
              <Tooltip
                position="top"
                openDelay={150}
                closeDelay={0}
                filter={(el) =>
                  !!(el && el.closest && el.closest(".has-tooltip"))
                }
              >
                <Grid
                  key={gridKey}
                  data={filteredAlerts}
                  dataItemKey="id"
                  pageable={true}
                  filterable={true}
                  sortable={true}
                  autoProcessData={true}
                  style={{ height: "600px" }}
                >
                  <GridColumn
                    field="id"
                    title="CVE"
                    width="150px"
                    filter="text"
                    cells={{ data: IDCell }}
                  />
                  <GridColumn
                    field="title"
                    title="Alert Message"
                    width="280px"
                    filter="text"
                    cells={{ data: TitleCell }}
                  />
                  <GridColumn
                    field="severity"
                    title="Severity"
                    width="120px"
                    cells={{ data: SeverityCell }}
                    filter="text"
                  />
                  <GridColumn
                    field="date"
                    title="Date"
                    width="150px"
                    cells={{ data: DateCell }}
                    filter="date"
                  />
                </Grid>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: 4 Quadrants - Structure révisée */}
        <div className="sora-right-panel">
          {/* Groupement flex pour cadrans 1 et 3 */}
          <div className="quadrant-flex-group">
            {/* Quadrant 1: Search */}
            <div className="sora-quadrant">
              <div className="section-card">
                <h4>🔍 Search Alerts</h4>
                <AutoComplete
                  placeholder="Search alerts..."
                  data={alerts.map((a) => a.title)}
                  value={search}
                  onChange={(e) => setSearch(e.value)}
                  style={{ width: "100%", marginBottom: "15px" }}
                />
                <Tooltip
                  position="top"
                  showOn="mouseenter"
                  openDelay={150}
                  closeDelay={0}
                  filter={(el) => !!(el && el.closest(".has-tooltip"))}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginTop: "auto",
                    }}
                  >
                    <Card style={{ padding: "15px", textAlign: "center" }}>
                      <div
                        className="has-tooltip"
                        title="Total alerts (all severities)"
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#3b82f6",
                          }}
                          aria-label="Total alerts value"
                        >
                          {alerts.length}
                        </div>
                        <div style={{ fontSize: "12px" }}>Total Alerts</div>
                      </div>
                    </Card>
                    <Card style={{ padding: "15px", textAlign: "center" }}>
                      <div
                        className="has-tooltip"
                        title="Critical alerts (High severity)"
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#ef4444",
                          }}
                          aria-label="High severity alerts value"
                        >
                          {alerts.filter((a) => a.severity === "High").length}
                        </div>
                        <div style={{ fontSize: "12px" }}>Critical</div>
                      </div>
                    </Card>
                  </div>
                </Tooltip>
              </div>
            </div>

            {/* Quadrant 3: Real-time Chart */}
            <h4 style={{ marginTop: 0 }}>📊 Real-time Activity</h4>
            <div className="sora-quadrant">
              <div className="section-card">
                <div className="sora-chart-container">
                  <RealTimeAlertChart
                    alerts={alerts}
                    setSeverityFilter={setSeverityFilter}
                  />
                </div>
              </div>
            </div>
            <LiveMonitoring />
          </div>

          {/* Cadran 2: Threat Distribution - AVEC DONNÉES VISIBLES */}
          <div className="sora-quadrant sora-quadrant-2">
            <div
              id="analytics-section"
              className="section-card"
              ref={analyticsSectionRef}
            >
              <h4>📊 Threat Distribution</h4>

              {/* Chart KendoReact (memoized) */}
              {threatChart}

              {/* LÉGENDE DÉTAILLÉE AVEC DONNÉES SUPPLEMENTAIRES - toujours visible */}
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  backgroundColor: "#f8f9fa",
                  border: "1px dashed #dee2e6",
                }}
              >
                <h5
                  style={{
                    margin: "0 0 10px 0",
                    color: "#495057",
                    textAlign: "center",
                  }}
                >
                  📝 Data Verification - Should Match Chart Above
                </h5>

                {/* Stats détaillées */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#dc3545",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {alerts.filter((a) => a.severity === "High").length}
                    </div>
                    <div style={{ fontSize: "12px", color: "#dc3545" }}>
                      High Severity
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "8px",
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#ffc107",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {alerts.filter((a) => a.severity === "Medium").length}
                    </div>
                    <div style={{ fontSize: "12px", color: "#ffc107" }}>
                      Medium Severity
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "8px",
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#28a745",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {alerts.filter((a) => a.severity === "Low").length}
                    </div>
                    <div style={{ fontSize: "12px", color: "#28a745" }}>
                      Low Severity
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "8px",
                      backgroundColor: "#e7f5ff",
                      borderRadius: "6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#0d6efd",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      {alerts.length}
                    </div>
                    <div style={{ fontSize: "12px", color: "#0d6efd" }}>
                      Total Alerts
                    </div>
                  </div>
                </div>

                {/* Pourcentages */}
                <div
                  style={{
                    padding: "10px",
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#495057",
                      marginBottom: "5px",
                    }}
                  >
                    📊 Percentage Distribution:
                  </div>
                  <div style={{ fontSize: "11px", color: "#6c757d" }}>
                    High:{" "}
                    {(
                      (alerts.filter((a) => a.severity === "High").length /
                        (alerts.length || 1)) *
                      100
                    ).toFixed(1)}
                    % • Medium:{" "}
                    {(
                      (alerts.filter((a) => a.severity === "Medium").length /
                        (alerts.length || 1)) *
                      100
                    ).toFixed(1)}
                    % • Low:{" "}
                    {(
                      (alerts.filter((a) => a.severity === "Low").length /
                        (alerts.length || 1)) *
                      100
                    ).toFixed(1)}
                    %
                  </div>
                </div>

                {/* Dernière alerte */}
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#fff3cd",
                    borderRadius: "6px",
                    fontSize: "11px",
                    color: "#856404",
                    textAlign: "center",
                  }}
                >
                  ⚡ Last alert:{" "}
                  {alerts.length > 0 ? alerts[0].title : "No alerts"} -{" "}
                  {alerts.length > 0 ? formatDate(alerts[0].date) : ""}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics - AI Analysis Section */}
      <div
        id="analytics-ai-section"
        style={{ padding: "4px 20px 0", maxWidth: "1400px", margin: "0 auto" }}
      >
        <div className="section-card" style={{ padding: "16px" }}>
          <h4 style={{ marginTop: 0 }}>🤖 AI Analysis</h4>
          <AIAnalyst alerts={alerts} />
        </div>
      </div>

      {/* Démo vidéo  */}
      <div style={{ padding: "40px 0", textAlign: "center" }}>
        <h3 style={{ marginBottom: "20px" }}>🎬 Demo Video</h3>
        <VideoDemo />
      </div>

      {/* SECTION SETTINGS */}

      {/* System Status Footer */}
      <div
        style={{
          textAlign: "center",
          padding: "20px",
          marginTop: "30px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <p style={{ margin: "10px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
          SOC-CERT Security Dashboard v1.0 | Real-time Threat Monitoring
        </p>
      </div>
    </div>
  );
}

export default App;
